import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Bell, Facebook, Instagram, Linkedin } from "lucide-react";
import GitchaLogo from "@/images/gitcha-logo.svg";
import { fetchUserDetails } from "@/actions/userprofile";
import { getUserId } from "@/helpers/common";
import { formatPrice, windowStateOf } from "@/components/GitchaProWindow";
import { useGitchaProOffer } from "@/hooks/useGitchaPro";
import useGitchaProCheckout from "@/hooks/useGitchaProCheckout";
import { formatPlanPrice, packageOf, usePlans } from "@/hooks/usePlans";
import { useAgentPublicProfile } from "@/hooks/useAgentPublicProfile";
import ProUpgrade from "@/components/ProUpgrade";
import { budgetPhrase, Face } from "@/components/ProUpgrade/ProArt";
import { glueLast, longDay, proStage, yearlyOption } from "@/components/ProUpgrade/proUpgradeModel";
import CancelPlanModal from "@/components/PlanUpgrade/CancelPlanModal";
import "@/components/ProUpgrade/ProUpgrade.scss";
import { PRO_FEATURES, PRO_INTRO } from "@/components/ProUpgrade/proBenefits";
import "./GitchaProPage.scss";

export { GITCHA_PRO_PATH } from "@/helpers/gitchaProPath";
export const SETTINGS_PROFILE_PATH = "/settings?tab=profile";

export const pageCopy = ({ stage, offer, pkg, brokerageName }) => {
  const monthlyAmount = offer?.price?.amount ?? pkg?.price?.monthly;
  const monthly = formatPlanPrice(monthlyAmount);
  // Yearly is only mentioned when it costs less than twelve months.
  const yearly =
    pkg?.price?.source === "seat" || !yearlyOption(monthlyAmount, pkg?.price?.yearly) ? null : formatPlanPrice(pkg?.price?.yearly);
  const both = yearly ? `${monthly} a month or ${yearly} a year` : `${monthly} a month`;
  if (stage === "offer") {
    return {
      title: "Your Gitcha bio, upgraded",
      price: monthly ? { strong: `Free for ${offer?.trialDays || 30} days`, rest: `, then ${both}.` } : null,
      action: "Try Gitcha Pro free",
      note: "No card today. We email you 5 days before the trial ends.",
    };
  }
  if (stage === "upgrade") {
    return { title: "Your Gitcha bio, upgraded", price: monthly ? { strong: both, rest: "." } : null, action: "Upgrade to Gitcha Pro", note: "Cancel anytime in Settings." };
  }
  if (stage === "trial") {
    const end = longDay(offer?.trialEndsOn);
    return { title: `Your Gitcha bio has Pro until ${end}`, price: monthly ? { strong: `Then ${both}`, rest: " if you keep it." } : null, action: "Keep Gitcha Pro", note: "Nothing is charged until your trial ends." };
  }
  if (stage === "pro") {
    const amount = pkg?.period === "year" ? `${formatPrice(pkg?.price?.yearly)} a year` : pkg?.price?.monthly ? `${formatPrice(pkg.price.monthly)} a month` : null;
    const renews = longDay(pkg?.renewsAt);
    const parts = [pkg?.planName, amount, renews ? `renews ${renews}` : null].filter(Boolean);
    return { title: "Your Gitcha bio has Pro", price: parts.length ? { strong: parts.join(", "), rest: "." } : null, action: "Manage", note: "Change or cancel it in Plans and billing." };
  }
  if (stage === "covered") {
    return { title: `${pkg?.coveredBy || brokerageName || "Your brokerage"} covers your Gitcha Pro`, price: null, action: null, note: "Nothing is billed to you." };
  }
  return null;
};

const BioPreview = ({ details, listing, office }) => (
  <div className="pro-page__bio" aria-hidden="true">
    <div className="pro-page__cover" />
    <div className="pro-page__who">
      <span className="pro-page__face">
        <Face details={details} />
      </span>
      <div>
        <div className="pro-page__name">
          {details?.FirstName} {details?.LastName}
        </div>
        <div className="pro-page__co">{office || "\u00a0"}</div>
      </div>
    </div>
    <div className="pro-page__blk">
      <span className="pro-page__mk">1</span>
      <div className="pro-page__blk-title">Tell {details?.FirstName || "me"} what you're looking for</div>
      <div className="pro-page__fld">Price range</div>
      <div className="pro-page__fld">Areas</div>
      <div className="pro-page__go">Send to {details?.FirstName || "me"}</div>
    </div>
    <div className="pro-page__blk">
      <span className="pro-page__mk">2</span>
      <div className="pro-page__wl">
        <span className="pro-page__vid" />
        <div>
          <b>{listing?.budget || "\u00a0"}</b>
          <span>{listing?.title || "Your Buyer Want‑Listing"}</span>
        </div>
      </div>
    </div>
    <div className="pro-page__shr">
      <span className="pro-page__mk">3</span>
      <span>Share this Buyer Want&#8209;Listing</span>
      <span className="pro-page__chips">
        <i>
          <Facebook size={14} strokeWidth={1.75} />
        </i>
        <i>
          <Linkedin size={14} strokeWidth={1.75} />
        </i>
        <i>
          <Instagram size={14} strokeWidth={1.75} />
        </i>
      </span>
    </div>
  </div>
);

const ResultCard = ({ details }) => (
  <div className="pro-page__result" aria-hidden="true">
    <span className="pro-page__result-icon">
      <Bell size={16} strokeWidth={1.75} />
    </span>
    <span>
      <b>A buyer used your form</b>
      <span>Sent to {details?.FirstName || "you"} from your bio</span>
    </span>
  </div>
);

const FeedBand = ({ details, listings, price, onPreview }) => (
  <section className="pro-page__feed" aria-labelledby="pro-page-feed-title">
    <div>
      <h2 id="pro-page-feed-title" className="pro-page__feed-title">
        Your Buyer Want&#8209;Listings on your own website
      </h2>
      <p className="pro-page__feed-copy">
        One line of code or the WordPress plugin. The cards take your site's fonts and colors, and every contact comes to you.
        {price ? ` ${price} a month, and you see your cards on your site before you pay.` : " You see your cards on your site before you pay."}
      </p>
      <button type="button" className="btn btn-outline-primary pro-page__feed-btn" onClick={onPreview}>
        Preview on my website
      </button>
    </div>
    <div className="pro-page__mini" aria-hidden="true">
      <div className="pro-page__mini-url">
        <span className="pro-page__mini-dots">
          <i />
          <i />
          <i />
        </span>{details?.websiteUrl ? String(details.websiteUrl).replace(/^https?:\/\//, "").replace(/\/$/, "") : "yourwebsite.com"}</div>
      <div className="pro-page__mini-grid">
        {(listings.length ? listings : [{ title: "Your Buyer Want‑Listings", budget: null }]).slice(0, 2).map((item, index) => (
          <div className="pro-page__mini-card" key={index}>
            <span className="pro-page__mini-map" />
            {item.budget ? <b>{item.budget}</b> : null}
            <span>{item.title}</span>
            <span className="pro-page__mini-btn">Contact {details?.FirstName || "me"}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GitchaProPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rows = useSelector((state) => state.userProfile?.userDetails);
  const details = Array.isArray(rows) ? rows[0] : null;
  const offerQuery = useGitchaProOffer(true);
  const plansQuery = usePlans(true);
  const offer = offerQuery.data;
  const pkg = packageOf(plansQuery.data, "pro");
  const feedPkg = packageOf(plansQuery.data, "agentFeed");
  const settled = Boolean(offer) && !plansQuery.isLoading;
  const stage = offerQuery.isError || plansQuery.isError ? "error" : settled ? proStage(windowStateOf(offer), pkg) : "loading";
  const loading = stage === "loading";
  const profile = useAgentPublicProfile(details?.slug).data;
  const listings = (profile?.activeWantAds || []).map((item) => ({ title: item.Title_Text, budget: budgetPhrase(item.Budget_mn, item.Budget_mx) }));
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const brokerageName = plansQuery.data?.audience?.brokerage?.name;
  const copy = pageCopy({ stage, offer, pkg, brokerageName });

  useEffect(() => {
    if (!details) {
      const userId = getUserId();
      if (userId) dispatch(fetchUserDetails({ userId }));
    }
  }, [details, dispatch]);

  useEffect(() => {
    if (params.get("keep") === "1" && (stage === "trial" || stage === "upgrade")) setOpen(true);
  }, [params, stage]);

  const checkout = useGitchaProCheckout({
    show: stage === "offer",
    onTrialStarted: (next) => {
      toast.dismiss();
      toast.success(`Gitcha Pro is on until ${longDay(next?.trialEndsOn) || "your trial ends"}.`);
    },
    onKept: () => {},
    onUpgraded: () => {},
  });

  const act = () => {
    if (stage === "offer") {
      checkout.setError(null);
      checkout.startTrial.mutate();
      return;
    }
    setOpen(true);
  };

  const closeUpgrade = () => {
    setOpen(false);
    if (params.get("keep")) {
      params.delete("keep");
      setParams(params, { replace: true });
    }
  };

  return (
    <div className="gitcha-pro-page">
      <div className="pro-page">
        <div className="pro-page__hero">
          <div className="pro-page__copy">
            <img className="pro-page__logo" src={GitchaLogo} alt="Gitcha" />
            {stage === "error" || stage === "unavailable" || (!loading && !copy) ? (
              <>
                <h1 className="pro-page__title">Gitcha Pro could not load right&nbsp;now</h1>
                <p className="pro-page__lead">Please try again in a moment.</p>
                <button type="button" className="btn btn-primary pro-page__cta" onClick={() => {
                    offerQuery.refetch();
                    if (plansQuery.isError) plansQuery.refetch();
                  }}>
                  Try again
                </button>
              </>
            ) : (
              <>
                {loading ? (
                  <span className="pro-page__title pro-page__ph pro-page__ph--title" aria-hidden="true" />
                ) : (
                  <h1 className="pro-page__title">{glueLast(copy.title)}</h1>
                )}
                <p className="pro-page__lead">{PRO_INTRO}</p>
                <ol className="pro-page__features">
                  {PRO_FEATURES.map((feature, index) => (
                    <li key={feature.title} className="pro-page__feature" style={{ "--i": index }}>
                      <span className="pro-page__num">{index + 1}</span>
                      <span>
                        <b>{feature.title}</b>
                        <span>{feature.body}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                {loading ? (
                  <p className="pro-page__price" aria-hidden="true">
                    <span className="pro-page__ph pro-page__ph--line" />
                  </p>
                ) : copy.price ? (
                  <p className="pro-page__price">
                    <b>{copy.price.strong}</b>
                    {copy.price.rest}
                  </p>
                ) : null}
                {!loading && checkout.error ? (
                  <p className="pro-upgrade__error" role="alert">
                    {checkout.error}
                  </p>
                ) : null}
                <div className="pro-page__actions" aria-busy={loading || undefined}>
                  {loading ? (
                    <>
                      <span className="pro-page__ph pro-page__ph--cta" aria-hidden="true" />
                      <span className="pro-page__note pro-page__ph pro-page__ph--note" aria-hidden="true" />
                    </>
                  ) : null}
                  {!loading && copy.action && stage !== "pro" ? (
                    <button type="button" className="btn btn-primary pro-page__cta" disabled={checkout.busy} onClick={act}>
                      {checkout.startTrial.isPending ? "Starting your trial" : copy.action}
                    </button>
                  ) : null}
                  {stage === "pro" ? (
                    <button type="button" className="btn btn-outline-primary pro-page__cta" onClick={act}>
                      Manage
                    </button>
                  ) : null}
                  {!loading && copy.note ? <p className="pro-page__note">{copy.note}</p> : null}
                </div>
              </>
            )}
          </div>
          <div className="pro-page__canvas">
            <div className="pro-page__stage">
              <BioPreview details={details} listing={listings[0]} office={brokerageName} />
              <ResultCard details={details} />
            </div>
          </div>
        </div>
        <FeedBand
          details={details}
          listings={listings}
          price={feedPkg?.price?.monthly ? formatPlanPrice(feedPkg.price.monthly) : null}
          onPreview={() => navigate(SETTINGS_PROFILE_PATH)}
        />
      </div>
      <ProUpgrade
        show={open}
        onHide={closeUpgrade}
        userDetails={details}
        initialView={stage === "upgrade" ? "keep" : null}
        onCancelPlan={() => {
          setOpen(false);
          setCancelling(true);
        }}
      />
      {cancelling ? <CancelPlanModal show={cancelling} onHide={() => setCancelling(false)} updateReloadParam={() => {}} userDetails={details} /> : null}
      {checkout.modals}
    </div>
  );
};

export default GitchaProPage;
